import React, {
    useEffect, useMemo, useState,
} from "react";
import { Dropdown, Form } from "react-bootstrap";
import { AiOutlinePlus, AiOutlineTag } from "react-icons/ai";
import { BiDownArrow, BiRightArrow } from "react-icons/bi";
import { BsChevronDoubleLeft, BsChevronDoubleRight, BsDot } from "react-icons/bs";
import { GoPlus } from "react-icons/go";
import { IoEllipsisVertical } from "react-icons/io5";
import { RiArticleLine } from "react-icons/ri";
import useRouting, { Link, Paths, useParams } from "../../contexts/Routing";
import {
    ArticleTag, GetArticleTagsDocument,
    useDeleteArticleTagMutation,
    useGetArticlesByTagQuery,
    useGetArticleTagsQuery,
    useSaveArticleMutation,
    useSaveArticleTagMutation,
} from "../../__generated__/api";
import Confirm from "../confirm/Confirm";
import { LoadingIcon } from "../loading/Loading";
import IconUploader from "../pickers/icon-uploader/IconUploader";
import ArticleTagsSearch from "./ArticleTagsSearch";
import Select, { Option } from "../select/Select";
import Styles from "./ArticleTags.module.scss";
import Resizable from "../resizable/Resizable";

export function DefaultArticleSelect({ tag, setPageId }:{
    tag: ArticleTag;
    setPageId: (pageId: string) => void;
}) {
    const { data: defaultArticleOptions } = useGetArticlesByTagQuery({
        variables: {
            tagId: tag?.id,
        },
    });

    const articleOptions = defaultArticleOptions?.articles?.results.map(article => ({
        label: article.title,
        value: article.id,
    })) ?? [];

    const [selectedValue, setSelectedValue] = useState<Option>();

    const handleSelected = (selected: Option) => {
        setSelectedValue(selected);
        setPageId(selected?.value || "");
    };

    useEffect(() => {
        const defaultArticle = defaultArticleOptions?.articles?.results.find(article => article.id === tag.indexPageId);

        const defaultSelectedValue = {
            label: defaultArticle?.title as string,
            value: defaultArticle?.id as string,
        } ?? defaultArticle;

        setSelectedValue(defaultSelectedValue);
    }, [tag]);

    return (
        <Select
            value={selectedValue}
            options={articleOptions}
            onChange={(option: Option) => handleSelected(option)}
        />
    );
}

export function EditTag({
    tag, shouldShow, onHide, id, parentId, isNewTag,
}: {
    tag?: ArticleTag;
    shouldShow: boolean;
    onHide: (() => void);
    id?: string;
    parentId?: string;
    isNewTag?: boolean;
}) {
    const { historyPush } = useRouting();
    const [saveTag] = useSaveArticleTagMutation();
    const [title, setTitle] = useState(tag?.title ?? "");
    const [description, setDescription] = useState(tag?.description);
    const [iconUrl, setIconUrl] = useState<string | undefined>(tag?.iconUrl ?? "");
    const [childIds, setChildIds] = useState<string[]>(tag?.childIds ?? []);
    const [indexPageId, setIndexPageId] = useState<string>(tag?.indexPageId ?? "");
    const [saveAnswer] = useSaveArticleMutation();

    useEffect(() => {
        setTitle(tag?.title ?? "");
        setDescription(tag?.description);
        setIconUrl(tag?.iconUrl ?? "");
        setChildIds(tag?.childIds ?? []);
        setIndexPageId(tag?.indexPageId ?? "");
    }, [tag, shouldShow]);

    const updateTag = () => {
        (async () => {
            await saveTag({
                refetchQueries: [
                    { query: GetArticleTagsDocument },
                ],
                variables: {
                    id,
                    articleTag: {
                        title,
                        description,
                        iconUrl,
                        childIds,
                        indexPageId,
                    },
                },
            });

            onHide();
        })();
    };

    const createTag = () => {
        (async () => {
            const newTag = await saveTag({
                refetchQueries: [
                    { query: GetArticleTagsDocument },
                ],
                variables: {
                    parentId,
                    articleTag: {
                        title,
                        description,
                        iconUrl,
                        childIds,
                    },
                },
            });

            const newTagData = newTag?.data?.saveArticleTag;

            const answer = await saveAnswer({
                variables: {
                    id,
                    article: {
                        title,
                        body: "<p></p>",
                        iconUrl,
                        tagIds: [newTagData?.id],
                    },
                },
            });

            const newArticleId = answer?.data?.saveArticle.id;

            await saveTag({
                refetchQueries: [
                    { query: GetArticleTagsDocument },
                ],
                variables: {
                    id: newTagData?.id,
                    articleTag: {
                        title: newTagData?.title ?? "",
                        description: newTagData?.description,
                        iconUrl: newTagData?.iconUrl,
                        childIds: newTagData?.childIds ?? [],
                        indexPageId: newArticleId,
                    },
                },
            });

            historyPush(Paths.ANSWERS_GET, {
                id: newArticleId,
            });

            onHide();
        })();
    };
    return (
        <Confirm shouldShow={shouldShow} onHide={onHide} onConfirm={isNewTag ? createTag : updateTag} confirmText="Save">
            <div>
                <h5 className="text-light">
                    Create a new tag
                </h5>
                <IconUploader
                    onChange={url => setIconUrl(url)}
                    value={iconUrl}
                    shouldStore
                    className="mx-auto text-center"
                    placeholder="Click or drag over to upload an icon"
                />
                <Form.Group className="mb-4">
                    <Form.Label className="fw-bolder">Name</Form.Label>
                    <Form.Control
                        value={title as string}
                        onChange={evt => {
                            setTitle(evt.target.value);
                        }}
                        placeholder="How to articles"
                        required
                    />
                    <Form.Text>
                        The name of the tag
                    </Form.Text>
                </Form.Group>
                <Form.Group className="mb-4">
                    <Form.Label className="fw-bolder">
                        Description
                        {" "}
                        <small className="text-light">(optional)</small>
                    </Form.Label>
                    <Form.Control
                        value={description as string}
                        onChange={evt => {
                            setDescription(evt.target.value);
                        }}
                        placeholder="How to articles"
                        as="textarea"
                    />
                    <Form.Text>
                        Optional description of the tag.
                    </Form.Text>
                </Form.Group>
                {!isNewTag && tag && (
                    <Form.Group className="mb-4">
                        <Form.Label className="fw-bolder">
                            Default Page
                            {" "}
                            <small className="text-light">(optional)</small>
                        </Form.Label>
                        <DefaultArticleSelect
                            tag={tag}
                            setPageId={setIndexPageId}
                        />
                    </Form.Group>
                )}
            </div>
        </Confirm>
    );
}
EditTag.defaultProps = {
    id: undefined,
    parentId: undefined,
    tag: undefined,
    isNewTag: false,
};

export interface Props {
    isReadOnly: boolean;
    className: string;
    selectedTags: ArticleTag[]
}

interface ArticleTagView {
    tag: ArticleTag;
    children: ArticleTagView[];
}

function createTags(tags: ArticleTag[]): ArticleTagView[] {
    const tagIds = new Set(tags.flatMap(group => group.childIds).map(row => row.toString()));
    const tagMap = tags.toIdMap("id");
    const roots = tags.filter(row => !tagIds.has(row.id));

    const createChildren = (tag: ArticleTag): ArticleTagView => ({
        tag,
        children: tag.childIds.map(id => tagMap[id]).filterNull().map(row => createChildren(row)).sortBy(row => row.tag.title),
    });

    return roots.map(tag => createChildren(tag));
}

const IS_EXPANDED_KEY = "answers-expanded";

function getExpandedSet(tags: ArticleTag[]): string[] {
    const value = localStorage.getItem(IS_EXPANDED_KEY);
    const expandedList = value ? JSON.parse(value) : [];
    const tagIds = tags.toIdList("id");
    return [...expandedList, ...tagIds] as string[];
}

function findAllParents(tagsOnArticle: ArticleTag[], allTags: ArticleTag[], parentTagsToExpand: ArticleTag[] = []): ArticleTag[] {
    if (tagsOnArticle.isEmpty || allTags.isEmpty) {
        return parentTagsToExpand;
    }
    const selectedIds = new Set(tagsOnArticle.toIdList("id"));
    const parentTags = allTags?.filter(tag => tag.childIds?.some(child => selectedIds.has(child))) as ArticleTag[];

    if (parentTags.isNotEmpty) {
        parentTagsToExpand.push(...parentTags);
        return findAllParents(parentTags, allTags, parentTagsToExpand);
    }
    return parentTagsToExpand;
}

function ArticleTagDisplay({ tag, isReadOnly, isExpandedSet }: {
    tag: ArticleTagView;
    isReadOnly: boolean;
    isExpandedSet: Set<string>
}) {
    const [deleteTag] = useDeleteArticleTagMutation();
    const { data: articlesByTag, loading } = useGetArticlesByTagQuery({
        variables: {
            tagId: tag.tag.id,
        },
    });
    const { id: selectedAnswerId } = useParams(Paths.ANSWERS_GET);

    const [isHovering, setIsHovering] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isAddChild, setIsAddChild] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const [isExpanded, setIsExpanded] = useState(() => isExpandedSet.has(tag.tag.id));

    useEffect(() => {
        setIsExpanded(() => isExpandedSet.has(tag.tag.id));
    }, [isExpandedSet]);
    useEffect(() => {
        if (isExpanded) {
            isExpandedSet.add(tag.tag.id);
        } else {
            isExpandedSet.delete(tag.tag.id);
        }
        localStorage.setItem(IS_EXPANDED_KEY, JSON.stringify(Array.from(isExpandedSet)));
    }, [isExpanded]);

    const onDelete = async () => {
        await deleteTag({
            refetchQueries: [
                { query: GetArticleTagsDocument },
            ],
            variables: {
                id: tag.tag.id,
            },
        });
        setIsDeleting(false);
    };

    return (

        <div
            key={tag.tag.id}
            className=" py-1"
        >
            {isDeleting && (
                <Confirm
                    confirmText="Delete"
                    onConfirm={onDelete}
                    shouldShow={isDeleting}
                    onHide={() => setIsDeleting(false)}
                    confirmVariant="danger"
                >
                    <div>
                        Are you sure you want to delete the tag:
                        {" "}
                        <div className="mt-2 p-2 rounded alert alert-danger">
                            {tag.tag.title}
                        </div>

                    </div>
                </Confirm>
            )}
            <EditTag
                shouldShow={isAddChild}
                onHide={() => setIsAddChild(false)}
                parentId={tag.tag.id}
                isNewTag
            />
            <EditTag
                shouldShow={isEditing}
                onHide={() => setIsEditing(false)}
                id={tag.tag.id}
                tag={tag.tag}
            />
            <div
                className=" d-flex bg-none align-items-center"
                onMouseEnter={!isReadOnly ? (() => setIsHovering(true)) : undefined}
                onMouseLeave={!isReadOnly ? (() => setIsHovering(false)) : undefined}
            >
                <div className="col-1 me-2 text-center">

                    <button
                        type="button"
                        onClick={() => setIsExpanded(value => !value)}
                        className="btn btn-none p-0"
                    >
                        {isExpanded ? <BiDownArrow /> : <BiRightArrow />}
                    </button>

                </div>

                <div className="col-1 me-2 text-center">
                    {tag.tag.iconUrl ? (
                        <img src={tag.tag.iconUrl as string} className="w-100" alt={tag.tag.title} />
                    ) : (
                        <AiOutlineTag className="fs-4" />
                    )}
                </div>
                <div className="col text-truncate">
                    {tag.tag.indexPageId ? (
                        <Link
                            to={Paths.ANSWERS_GET}
                            key={tag.tag.indexPageId}
                            params={{
                                id: tag.tag.indexPageId ?? "",
                            }}
                            className="d-flex bg-none align-items-center text-decoration-none text-gray-800 py-1"
                        >
                            {tag.tag.title}
                        </Link>
                    ) : (
                        <div className="d-flex bg-none align-items-center text-decoration-none text-gray-800 py-1">
                            {tag.tag.title}
                        </div>
                    )}
                </div>

                {isHovering && (
                    <Dropdown
                        className="p-0 m-0 text-light"
                    >
                        <Dropdown.Toggle variant="" className="text-light-400 p-0 menu-small btn-none me-1">
                            <IoEllipsisVertical />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Link to={Paths.ANSWERS_NEW} search={{ tagId: tag.tag.id }} className="dropdown-item">
                                New Page
                            </Link>
                            <Dropdown.Item
                                onClick={() => setIsAddChild(true)}
                            >
                                Add Tag
                            </Dropdown.Item>
                            <Dropdown.Item
                                onClick={() => setIsEditing(true)}
                            >
                                Edit
                            </Dropdown.Item>
                            <Dropdown.Item
                                onClick={() => setIsDeleting(true)}
                            >
                                Delete
                            </Dropdown.Item>

                        </Dropdown.Menu>
                    </Dropdown>
                )}

            </div>
            {(isExpanded && !loading) && (
                <div className="mt-2 ps-4">
                    {tag.children.isNotEmpty && (
                        // eslint-disable-next-line @typescript-eslint/no-use-before-define
                        <ArticleTagsList tags={tag.children} isReadOnly={isReadOnly} isExpandedSet={isExpandedSet} />
                    )}
                    {articlesByTag?.articles.results.sortBy(row => row.title?.toLowerCase()).map(article => (
                        <Link
                            to={Paths.ANSWERS_GET}
                            key={article.id}
                            params={{
                                id: article.id,
                            }}
                            className={`d-flex bg-none align-items-center text-decoration-none text-gray-800 py-1 ${
                                selectedAnswerId === article.id ? "bg-gray-200 rounded px-1 ms-n1 me-n1" : ""}`}
                        >
                            <div className="col-1 me-2 text-center">
                                <BsDot />
                            </div>

                            <div className="me-2 text-center">
                                {article.iconUrl ? (
                                    <img src={article.iconUrl as string} className={`w-100 ${Styles.icon}`} alt={article.title} />
                                ) : (
                                    <RiArticleLine className="fs-5" />
                                )}
                            </div>
                            <div className="col text-truncate">
                                {article.title || <span className="text-light st-italic">Untitled</span>}
                            </div>
                        </Link>
                    ))}

                    {!isReadOnly && tag.children.isEmpty && (
                        <Link
                            to={Paths.ANSWERS_GET}
                            params={{
                                id: "new",
                            }}
                            search={{ tagId: tag.tag.id }}
                            className={`d-flex bg-none align-items-center text-decoration-none text-gray-800 `}
                        >
                            <div className="col-1 me-2 text-light">
                                <GoPlus />
                            </div>

                            <div className="col text-truncate text-light fst-italic">
                                New Page
                            </div>
                        </Link>
                    )}

                </div>
            )}

        </div>
    );
}

interface ArticleTagProps {
    tags: ArticleTagView[];
    isReadOnly: boolean;
    isExpandedSet: Set<string>;
}

export function ArticleTagsList({ tags, isReadOnly, isExpandedSet }: ArticleTagProps) {
    return (
        <div className="list-group">
            {tags?.mapFilter(tag => (
                <div key={tag.tag.id}>
                    <ArticleTagDisplay tag={tag} isReadOnly={isReadOnly} isExpandedSet={isExpandedSet} />
                </div>
            ))}
        </div>
    );
}

export default function ArticleTags({ selectedTags, isReadOnly, className }: Props) {
    const { data, loading } = useGetArticleTagsQuery();
    const [isAddingTag, setIsAddingTag] = useState(false);
    const [isHidden, setIsHidden] = useState(false);
    const [isSearching, setIsSearching] = useState(false);

    const tagTree = useMemo(() => (data?.articleTags ? createTags(data?.articleTags) : undefined), [data?.articleTags]);
    const parentsToExpand = useMemo(() => findAllParents(selectedTags, data?.articleTags ?? []), [selectedTags]);
    const tagsToExpand = [...parentsToExpand, ...selectedTags];

    if (isReadOnly && !data?.articleTags.isNotEmpty) {
        return null;
    }
    if (loading || !tagTree) {
        return <LoadingIcon />;
    }

    if (isHidden) {
        return (
            <button
                type="button"
                className={`btn btn-blue-dark btn-sm text-white
                    position-absolute opacity-50 bottom-0 fs-5 rounded-0 rounded-end ${Styles["tags-expand"]}`}
                onClick={() => setIsHidden(value => !value)}
            >
                <BsChevronDoubleRight />
            </button>
        );
    }

    return (
        <Resizable
            direction="e"
            maxSize={400}
            minSize={150}
            defaultSize={250}
        >
            <div className={`p-3 position-relative ${className}`}>

                <EditTag
                    onHide={() => setIsAddingTag(false)}
                    shouldShow={isAddingTag}
                    tag={undefined}
                    isNewTag
                />

                <div className="d-flex justify-content-between align-items-center mb-2">
                    <button
                        type="button"
                        className="btn btn-sm btn-outline text-light"
                        onClick={() => setIsHidden(value => !value)}
                    >
                        <BsChevronDoubleLeft />
                    </button>
                    {!isReadOnly && (
                        <button
                            type="button"
                            className="btn btn-none btn-sm text-light"
                            onClick={() => setIsAddingTag(true)}
                        >
                            <AiOutlinePlus />
                            {" "}
                            Tag
                        </button>
                    )}

                </div>

                {!isReadOnly && !data?.articleTags.isNotEmpty && (
                    <div className="mt-3 text-center">
                        <small className="text-light d-block ">
                            There are currently no tags. Create a tag and associate articles to it. This helps users
                            find your articles and discover new answers for themselves.
                        </small>
                        <button
                            type="button"
                            className="btn btn-outline btn-sm text-light mt-3"
                            onClick={() => setIsAddingTag(true)}
                        >
                            <AiOutlinePlus />
                            {" "}
                            Tag
                        </button>
                    </div>
                )}
                <ArticleTagsSearch onSearching={() => setIsSearching(true)} onDoneSearching={() => setIsSearching(false)} />
                {!isSearching && (
                    <ArticleTagsList
                        tags={tagTree}
                        isReadOnly={isReadOnly}
                        isExpandedSet={new Set(getExpandedSet(tagsToExpand))}
                    />
                )}

            </div>
        </Resizable>

    );
}
